/**
 * DataBest — Login page.
 * F1: real JWT login flow. Redirects to /dashboard on success.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LockKeyhole, Mail, Sparkles } from "lucide-react";
import AnimatedBackground from "@/features/auth/components/AnimatedBackground";
import { DataBestLogoShieldNetwork } from "@/components/DataBestLogo";
import useAuthStore from "@/store/authStore";
import { landingPathForRole } from "@/lib/landing";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, user } = useAuthStore();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState(null);

    // If already logged in, bounce to role-appropriate landing
    useEffect(() => {
        if (isAuthenticated()) {
            navigate(landingPathForRole(user?.role), { replace: true });
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatusMsg(null);
        try {
            const loggedUser = await login(email.trim().toLowerCase(), password);
            const from =
                location.state?.from?.pathname ||
                landingPathForRole(loggedUser?.role);
            navigate(from, { replace: true });
        } catch (err) {
            setStatusMsg({
                kind: "error",
                text: err?.message || "Login failed. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative min-h-[100dvh] w-full overflow-hidden"
            data-testid="login-page"
        >
            <AnimatedBackground />

            <div className="relative z-10 flex min-h-[100dvh] flex-col">
                {/* Top brand bar */}
                <header className="flex items-center justify-between px-6 py-6 md:px-12 md:py-8">
                    <DataBestLogoShieldNetwork
                        size={44}
                        withWordmark
                        dark
                        variantId="header"
                    />
                    <div className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.28em] text-white/70 backdrop-blur md:flex">
                        <Sparkles className="h-3.5 w-3.5 text-brand-green-300" />
                        <span className="font-heading font-semibold">
                            Preview · Live
                        </span>
                    </div>
                </header>

                <main className="flex flex-1 flex-col items-stretch gap-10 px-6 pb-8 md:flex-row md:items-center md:justify-between md:gap-14 md:px-16 md:pb-16">
                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-xl text-white"
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-green-200 backdrop-blur"
                            data-testid="login-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-green-400 animate-shimmer-slow" />
                            Individual Evaluation and Progress
                        </span>
                        <h1 className="mt-8 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-balance md:text-6xl">
                            Transforming Care Through{" "}
                            <span className="text-brand-green-300">
                                Structured Insight
                            </span>
                            .
                        </h1>
                        <p className="mt-6 max-w-lg text-base leading-relaxed text-white/75 md:text-lg">
                            DataBest turns every caregiver–individual
                            interaction into measurable, standards-aligned
                            documentation — from the ISP plan down to the daily
                            task. Built for Virginia DD/IDD providers,
                            HIPAA-ready and fully auditable.
                        </p>

                        <dl className="mt-10 grid grid-cols-3 gap-6 border-t border-white/10 pt-6 text-white/85">
                            <div>
                                <dt className="text-[11px] uppercase tracking-[0.22em] text-white/50">
                                    Framework
                                </dt>
                                <dd className="mt-1 font-heading text-lg font-semibold">
                                    BIRP
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] uppercase tracking-[0.22em] text-white/50">
                                    Dimensions
                                </dt>
                                <dd className="mt-1 font-heading text-lg font-semibold">
                                    4 Axes
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] uppercase tracking-[0.22em] text-white/50">
                                    Compliance
                                </dt>
                                <dd className="mt-1 font-heading text-lg font-semibold">
                                    HIPAA · DBHDS
                                </dd>
                            </div>
                        </dl>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.8,
                            delay: 0.15,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="w-full max-w-md"
                    >
                        <div
                            className="glass-surface rounded-2xl p-8 md:p-10"
                            data-testid="login-card"
                        >
                            <div className="mb-8">
                                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-blue-700">
                                    Welcome back
                                </p>
                                <h2 className="mt-2 font-heading text-2xl font-bold text-brand-blue-950 md:text-3xl">
                                    Sign in to DataBest
                                </h2>
                                <p className="mt-2 text-sm text-slate-600">
                                    Use your provider-issued credentials to
                                    continue.
                                </p>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                className="space-y-5"
                                data-testid="login-form"
                            >
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
                                    >
                                        Email
                                    </label>
                                    <div className="relative">
                                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <input
                                            id="email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="you@provider.org"
                                            data-testid="login-email-input"
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-white/90 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <label
                                            htmlFor="password"
                                            className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
                                        >
                                            Password
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword((s) => !s)
                                            }
                                            className="text-[11px] font-semibold uppercase tracking-wider text-brand-blue-700 hover:text-brand-blue-800"
                                            data-testid="login-toggle-password"
                                        >
                                            {showPassword ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <LockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <input
                                            id="password"
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            required
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder="••••••••"
                                            data-testid="login-password-input"
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-white/90 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="login-submit-button"
                                    className="group relative flex h-12 w-full items-center justify-center overflow-hidden rounded-xl bg-brand-blue-700 text-sm font-bold uppercase tracking-[0.18em] text-white shadow-md transition-transform hover:bg-brand-blue-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Signing in…
                                        </>
                                    ) : (
                                        <>Sign in</>
                                    )}
                                </button>

                                {statusMsg && (
                                    <div
                                        role="status"
                                        data-testid="login-status-message"
                                        className={`rounded-xl border px-4 py-3 text-sm ${
                                            statusMsg.kind === "info"
                                                ? "border-brand-blue-200 bg-brand-blue-50 text-brand-blue-800"
                                                : "border-red-200 bg-red-50 text-red-800"
                                        }`}
                                    >
                                        {statusMsg.text}
                                    </div>
                                )}

                                <p className="pt-2 text-center text-xs text-slate-500">
                                    Need access? Contact your provider
                                    administrator.
                                </p>
                            </form>
                        </div>
                    </motion.section>
                </main>

                <footer
                    className="relative z-10 border-t border-white/10 bg-black/30 px-6 py-5 backdrop-blur md:px-16"
                    data-testid="brand-footer"
                >
                    <div className="flex flex-col items-start justify-between gap-3 text-white/60 md:flex-row md:items-center">
                        <div className="flex items-center gap-3">
                            <DataBestLogoShieldNetwork
                                size={28}
                                variantId="footer"
                            />
                            <span className="text-xs">
                                © {new Date().getFullYear()} DataBest —
                                Individual Evaluation and Progress System
                            </span>
                        </div>
                        <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.22em]">
                            <span>HIPAA-ready</span>
                            <span aria-hidden>·</span>
                            <span>Virginia DBHDS</span>
                            <span aria-hidden>·</span>
                            <span>Auditable by design</span>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default LoginPage;
